import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  DOCUMENT,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { IonRouterOutlet, NavController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import { interval, Observable, Subscription } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { Settings } from "src/model/class/settings";
import { SupervisionListFontSize } from "src/model/enum/enumCustomInterface";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { TypeBrightness } from "src/model/enum/enumTypeBrightness";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { AuthenticationService } from "src/service/authentication.service";
import { BIGService } from "src/service/big.service";
import { EventService } from "src/service/event.service";
import { GatewayService } from "src/service/gateway.service";
import { LoggerService } from "src/service/logger.service";
import { MqttClientService } from "src/service/mqtt-client.service";
import { NetworkService } from "src/service/network.service";
import { SecuretyDataProtectionService } from "src/service/securety-data-protection.service";
import { ThemeService } from "src/service/theme.service";
import { TranslationService } from "src/service/translation.service";
import { clientSettingsUpdateSupervisionFontSizeAction } from "src/state/actions/client-settings.actions";
import { isHydrated } from "src/state/hydrate";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import italianLanguage from "./../assets/i18n/it.json";
import italianLanguageSharedComponents from "src/components/shared-components/assets/i18n/it.json";
import englishLanguage from "./../assets/i18n/en.json";
import englishLanguageSharedComponents from "src/components/shared-components/assets/i18n/en.json";
import frenchLanguage from "./../assets/i18n/fr.json";
import frenchLanguageSharedComponents from "src/components/shared-components/assets/i18n/fr.json";
import spanishLanguage from "./../assets/i18n/es.json";
import spanishLanguageSharedComponents from "src/components/shared-components/assets/i18n/es.json";
import germanLanguage from "./../assets/i18n/de.json";
import germanLanguageSharedComponents from "src/components/shared-components/assets/i18n/de.json";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { environment } from "src/environments/environment";

import { AutocloseOverlaysService } from "src/service/autoclose-overlays.service";
import { MenuPreferenceService } from "src/service/menu-preference.service";
import { SystemRouterService } from "src/service/system-router.service";
@Component({
    selector: "app-root",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent
  implements OnDestroy, OnInit, iTranslation, iEvent, iMqttHandling
{
  private _logContext: string = "App.component";
  private _translateSubscription: Subscription;
  public _eventSubscriptions: Array<Subscription> = null;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  private _currentConnectionType: ConnectionType = ConnectionType.NONE;
  private _lblChangedPermissionTitle: string = "";
  private _lblChangedPermissionText: string = "";
  private _username = "";
  private _currentLoggedUser: CurrentSessionData = null;
  private _currentUserSettings: Settings = null;

  private _currentClientSetting: ClientSettings = null;
  private _isInInitialisation: boolean = true;

  private _lastShowStatusBar: boolean = true;
  private _proximitySensor: boolean = false;
  private _activateBrightness: boolean = true;
  private _lastDefaultBrightness: number = null;

  private _backButtonSubscription: Subscription = null;
  private _isCordovaReady: boolean = false;

  /** Longest wait for the startup sequence before the native splash screen is hidden anyway */
  private static readonly SPLASH_WATCHDOG_MS: number = 15000;
  // The native splash screen is dismissed exactly once, by whichever comes first between the end of the
  // startup sequence and the watchdog
  private _isSplashScreenDismissed: boolean = false;
  private _splashWatchdogTimer?: ReturnType<typeof setTimeout>;

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectCurrentUserSub: Subscription = null;
  private _selectClientSettingsSub: Subscription = null;
  private _selectServerSettingsSub: Subscription = null;
  private _subscriptionTimer: Subscription = null;
  //#endregion

  private _isDesktop: boolean = false;

  public showStatusBar = false;
  public showMenuWeb: boolean = false;

  /** Pinned menu: kept open on screens at least 992px wide (never on phones) */
  public get splitPaneWhen(): string | boolean {
    return this.showMenuWeb && this._menuPreferenceService.isPinned ? "(min-width: 992px)" : false;
  }
  public continueTimer: boolean = true;
  public contatoreSecondi: number = 0;

  @ViewChild(IonRouterOutlet, { static: true })
  ionRouterOutlet: IonRouterOutlet;

  public footerHeight: string = "";

  constructor(
    private _platform: Platform,
    private _bigService: BIGService,
    private _themeService: ThemeService,
    private _loggerService: LoggerService,
    private _translate: TranslationService,
    private _eventService: EventService,
    private _authService: AuthenticationService,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _networkService: NetworkService,
    private _gatewayService: GatewayService,
    private _mqttService: MqttClientService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _router: Router,
    private _navController: NavController,
    private _mqttServiceNative: MqttService,
    private _store: Store,
    private _dc: ChangeDetectorRef,
    private _autocloseOverlaysService: AutocloseOverlaysService,
    @Inject(DOCUMENT) private document: Document,
    private _menuPreferenceService: MenuPreferenceService,
    private _systemRouterService: SystemRouterService,
  ) {
    //this.handleCookieManager();
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    // Concat translations from Digital Place and Shared Components
    const italian = {
      dictionary: {
        ...italianLanguage.dictionary,
        ...italianLanguageSharedComponents.dictionary,
      },
    };
    const english = {
      dictionary: {
        ...englishLanguage.dictionary,
        ...englishLanguageSharedComponents.dictionary,
      },
    };
    const french = {
      dictionary: {
        ...frenchLanguage.dictionary,
        ...frenchLanguageSharedComponents.dictionary,
      },
    };
    const spanish = {
      dictionary: {
        ...spanishLanguage.dictionary,
        ...spanishLanguageSharedComponents.dictionary,
      },
    };
    const german = {
      dictionary: {
        ...germanLanguage.dictionary,
        ...germanLanguageSharedComponents.dictionary,
      },
    };

    this._translateService.setTranslation("it", italian);
    this._translateService.setTranslation("en", english);
    this._translateService.setTranslation("fr", french);
    this._translateService.setTranslation("es", spanish);
    this._translateService.setTranslation("de", german);

    this._translateService.setFallbackLang("it");

    this.initializeApp();
    this.subscribeSettingServiceData();
    this.subscribeUserSettings();

    this._isInInitialisation = true;

    // Check if the device is Android and its version in order to add space for virtual keyboard in Android >= 15 with cordova plugin device
    const onDeviceReady = () => {
      if (
        (window as any).device.platform === "Android" &&
        parseFloat((window as any).device.version) >= 15
      ) {
        this.addSpaceForVirtualKeyboard();
      }
    };

    document.addEventListener("deviceready", onDeviceReady, false);

    this._isDesktop = !(
      this._platform.is("ios") || this._platform.is("android")
    );
  }

  ngOnDestroy() {
    this._translateSubscription.unsubscribe();

    this.unsubscribeStoreSelect();

    this._backButtonSubscription.unsubscribe();

    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  ngAfterViewInit(): void {
    this._systemRouterService.registerRootOutlet(this.ionRouterOutlet);

    //if it's desktop app get footer height and resize the page dynamically
    const ionAppElement = document
      .querySelector("app-root")
      .querySelector("ion-app");

    // componentOnReady() exists only on the web components of the lazy Ionic build: the standalone one
    // defines them up front, so waiting for the definition is enough.
    customElements.whenDefined("ion-app").then(() => {
      if (
        ionAppElement.classList.contains("ion-page-with-footer-bar-dp") &&
        this._isDesktop
      ) {
        window.addEventListener("resize", () => {
          this.footerHeight =
            document
              .querySelector("app-footer-dp>footer")
              .clientHeight.toString() + "px";
        });
      }
    });
  }

  /**
   * Handle cookie manager
   */
  private handleCookieManager() {
    if (environment.cbid != "") {
      const headElement = this.document.getElementsByTagName("head")[0];
      const scriptElement = this.document.createElement("script");
      scriptElement.src = "https://consent.cookiebot.com/uc.js";
      scriptElement.id = "Cookiebot";
      scriptElement.dataset.cbid = environment.cbid;
      scriptElement.type = "text/javascript";
      headElement.appendChild(scriptElement);
    }
  }

  /**
   * Start time for brightness
   */
  private startTimer(): void {
    if (this._bigService.isMyToDevice) {
      const source = interval(1000);
      this._subscriptionTimer = source.subscribe((val) =>
        this.checkBrightness(),
      );
    }
  }
  //
  /**
   * Set default brightness
   */
  private checkDefaultBrightness() {
    if (
      this._currentClientSetting.defaultBrightness !=
      this._lastDefaultBrightness
    ) {
      this._bigService.setBrightnessInactivity(
        this._currentClientSetting.defaultBrightness,
      );
      this._lastDefaultBrightness =
        this._currentClientSetting.defaultBrightness;
    }
  }

  /**
   * Check timer and reduce brightness
   */
  private checkBrightness() {
    this.contatoreSecondi++;
    if (this.contatoreSecondi >= this._currentClientSetting.timeBrightness) {
      if (
        this._activateBrightness &&
        this._currentClientSetting?.enableBrightnessSettings &&
        this._currentClientSetting.typeBrightnessSettings ==
          TypeBrightness.INACTIVITY &&
        this._currentClientSetting.timeBrightness > 0
      ) {
        this._activateBrightness = false;
        this._bigService.setBrightnessInactivity(
          this._currentClientSetting.percentageBrightness,
        );
      }
    }
  }

  ngOnInit() {}

  async initializeApp() {
    this.startSplashWatchdog();

    try {
      this._themeService.setApplicationTheme();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "initializeApp()",
        "START",
      ); //
      this.subscribeEvents();

      await this.checkIsStoreReady();

      //this._themeService.setApplicationTheme(this._dc);

      const readySource: string = await this._platform.ready();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_platform.ready",
        "initializeApp()",
        "READY -->: " + readySource,
      );

      if (readySource === "cordova") {
        try {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_platform.ready",
            "initializeApp()",
            "Initialise CORDOVA plugins",
          );

          this._eventService.OnCordovaIsReadyToRumble.next(true);
          this._isCordovaReady = true;
        } catch (error) {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_platform.ready",
            "initializeApp()",
            "Error: " + error.message,
          );
        }
      }

      // SiboHandler removed: was throwing UNEXPECTED_ERROR on non-Sibo devices
      // and blocking app initialization (splash never hidden).
      // await this._bigService.initializeSiboVariableDevice();
      await this._translate.loadDefaultLanguage();
      if (this._bigService.isMyToDevice) {
        this._bigService.setBrightnessInactivity(
          this._currentClientSetting.defaultBrightness,
        );
      }
      this.setStartupAppStyle();

      this.loadTranslation();
      this.subscribeTranslationEvent();

      // SiboHandler removed
      // this.turnOffSiboLed();

      this.subscribeNetworkServiceData();
      this.handleUserRoutesAndData();

      this.handleFooterMenuOnReloadSystem(location.pathname);

      this.handleBackButton();
      this._eventService.OnAppInitialisationCompleted.next(true);

      this.startTimer();
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "initializeApp()",
        "Error: " + exception.message,
      );
    } finally {
      /* Reached whatever happened above: `launchAutoHide` is false in the Capacitor configuration, so
         anything thrown before this point would leave the native splash on screen for good. */
      this.dismissSplashScreen("startup sequence ended");
    }

    if (this._platform.is("android")) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "initializeApp()",
        "We are in ANDROID",
      );
      this._eventService.OnIsAndroidDevice.next(true);
    } else {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "initializeApp()",
        "We are NOT in ANDROID",
      );
    }
  }

  /**
   * Hides the native splash screen should the startup sequence never reach its end (an await that never
   * settles): `launchAutoHide` is false in the Capacitor configuration, so without this the splash would stay
   * on screen for good. Longer than the 5 s of BIGStudio3, which shows a web veil under its splash: here the app
   * itself is under it, and a slow device would get the blank page this splash is there to hide.
   */
  private startSplashWatchdog(): void {
    this._splashWatchdogTimer = setTimeout(() => {
      this._loggerService.log(
        LogLevel.LOG_WARNING,
        this._logContext,
        this._logContext,
        "startSplashWatchdog()",
        "Startup did not complete within " + AppComponent.SPLASH_WATCHDOG_MS + " ms",
      );
      this.dismissSplashScreen("watchdog expired");
    }, AppComponent.SPLASH_WATCHDOG_MS);
  }

  /**
   * Hides the native splash screen, once: whichever comes first between the end of the startup sequence and
   * the watchdog.
   *
   * @param reason why the splash is dismissed, for the log
   */
  private dismissSplashScreen(reason: string): void {
    if (this._isSplashScreenDismissed) {
      return;
    }
    this._isSplashScreenDismissed = true;

    clearTimeout(this._splashWatchdogTimer);
    this._splashWatchdogTimer = undefined;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "dismissSplashScreen()",
      "Splash screen dismissed: " + reason,
    );

    // In the browser there is no splash screen, and the plugin rejects the call
    if (Capacitor.isNativePlatform()) {
      void SplashScreen.hide();
    }
  }

  /**
   * Detect click event on DOM
   */
  @HostListener("document:click", ["$event"])
  public async onClickDOM(event): Promise<void> {
    this.onClickSetBrightness();
  }

  @HostListener("touchstart", ["$event"]) onTouchStart(event: Event): void {
    this.onClickSetBrightness();
  }

  @HostListener("touchmove", ["$event"]) onTouchMove(event: Event): void {
    this.onClickSetBrightness();
  }

  /**
   * Close modal, action sheet, alert and popover when user navigates with browser arrows
   */
  @HostListener("window:popstate", ["$event"]) onPopState(): void {
    this._autocloseOverlaysService.closeActiveOverlays();
  }

  /**
   * Set the brightness of the display
   */
  public onClickSetBrightness(): void {
    if (this._bigService.isMyToDevice) {
      if (
        this._currentClientSetting?.enableBrightnessSettings &&
        this._currentClientSetting.typeBrightnessSettings ==
          TypeBrightness.INACTIVITY
      ) {
        this.contatoreSecondi = 0;
        if (!this._activateBrightness) {
          this._activateBrightness = true;
          this._bigService.setBrightnessInactivity(
            this._currentClientSetting.defaultBrightness,
          );
        }
      }
    }
  }

  //#region Translation

  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.changedPermissionTitle",
        "dictionary.changedPermissionText",
      ])
      .subscribe((translation) => {
        this._lblChangedPermissionTitle =
          translation["dictionary.changedPermissionTitle"];
        this._lblChangedPermissionText =
          translation["dictionary.changedPermissionText"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      },
    );
  }
  //#endregion

  //v2
  // we have to show the status bar only after login, so
  // - if we don't have done the login: we hide the status bar and the height of the page content is 100%
  // - if we have done the login: we show the status bar and add to the .ion-page component the class .ion-page-with-footer-bar that
  //    remove the space of the status bar from the height
  // the class ion-page-with-footer-bar is assigned to the first ion-app tag found inside the app-root
  // <app-root><ion-app>...page content...</ion-app><app-footer></app-footer></app-root>

  public changeShowStatusBar(statusBarVisibility: boolean): void {
    //NOTE: If the current theme of app is "DIGITAL_PLACE", no Status Bar shall be showed
    this.showStatusBar = false;
    this.changeStatusBarDOMClassDP();
  }

  /**
   * Update ion app class adding/removing class to riseze page content
   * base don show status bar flag
   *
   */
  private changeStatusBarDOMClassDP(): void {
    const ionAppElement = document
      .querySelector("app-root")
      .querySelector("ion-app");

    if (ionAppElement) {
      if (
        ionAppElement &&
        !ionAppElement.classList.contains("ion-page-with-footer-bar-dp") &&
        this._isDesktop
      ) {
        ionAppElement.classList.add("ion-page-with-footer-bar-dp");
      }
    } else {
      if (
        ionAppElement &&
        ionAppElement.classList.contains("ion-page-with-footer-bar-dp") &&
        !this._isDesktop
      ) {
        ionAppElement.classList.remove("ion-page-with-footer-bar-dp");
      }
    }
  }

  /**
   * Update ion app class adding/removing class to riseze page content
   * base don show status bar flag
   *
   * @param showStatusBar Handle if status bar is visible
   */
  private changeStatusBarDOMClass(showStatusBar: boolean): void {
    const ionAppElement = document
      .querySelector("app-root")
      .querySelector("ion-app");

    if (ionAppElement) {
      if (showStatusBar) {
        if (
          ionAppElement &&
          !ionAppElement.classList.contains("ion-page-with-footer-bar")
        ) {
          ionAppElement.classList.add("ion-page-with-footer-bar");
        }
      } else {
        if (
          ionAppElement &&
          ionAppElement.classList.contains("ion-page-with-footer-bar")
        ) {
          ionAppElement.classList.remove("ion-page-with-footer-bar");
        }
      }
    }
  }

  private changeShowMenuAside(isVisible: boolean) {
    this.showMenuWeb = isVisible;
  }

  /**
   * Setting up start-up app style
   */
  private setStartupAppStyle() {
    this.changeShowStatusBar(false);

    if (Capacitor.isNativePlatform()) {
      void StatusBar.setOverlaysWebView({ overlay: false });
    }
    this.setNativeStatusBarColor(this._themeService.getMainColorStatusBar());
    if (Capacitor.isNativePlatform()) {
      // Light content on a dark background, as styleLightContent() of the Cordova plugin
      void StatusBar.setStyle({ style: Style.Dark });
    }
  }

  /**
   * Paint the native status bar. In the browser there is no status bar and the plugin rejects the call.
   *
   * @param color colour in #RRGGBB
   */
  private setNativeStatusBarColor(color: string): void {
    if (Capacitor.isNativePlatform()) {
      void StatusBar.setBackgroundColor({ color });
    }
  }

  /**
   * Set current native status bar color based on current auth user
   * and current network status
   */
  private setStatusBarAndToolbarColor() {
    const toolbar = document.querySelector(".toolbar-title-app");

    if (
      this._currentLoggedUser &&
      this._currentConnectionType === ConnectionType.NONE
    ) {
      this.setNativeStatusBarColor(this._themeService.getMainColorStatusOffline());

      if (toolbar) {
        toolbar.classList.add("offline");
      }
    } else {
      this.setNativeStatusBarColor(this._themeService.getMainColorStatusBar());

      if (toolbar) {
        toolbar.classList.remove("offline");
      }
    }
  }

  //v2
  /**
   * Turn off sibo led
   */
  private turnOffSiboLed() {
    // Sibo devices start with the red led on: change the color of the led to off on start up
    this._bigService.changeColorLed(0, 0, 0);
  }

  //#region Subscribe to event-service observable
  //
  //private subscribeEvent() {
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();
    /**
     * Reaction to the successful completion of logging-in operation (to a plant)
     */
    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(async () => {
        if (!this._currentClientSetting.supervisionFontSize) {
          this._store.dispatch(
            clientSettingsUpdateSupervisionFontSizeAction({
              fontSize: SupervisionListFontSize.MEDIUM,
            }),
          );
        }
        //

        /**
         * DONT'T TOUCH IT: gateway initialization must be done before sibo initialization
         */
        const areGatewaysReady: boolean =
          await this._gatewayService.Initialize();


        this.subscribeMQTTAddresses();
      }),
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 1.",
        );
        for (const entry of Array.from(
          new Map(
            Object.entries(this._mqttServiceNative.observables),
          ).entries(),
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 1 Entry:  " + entry[0],
          );
        }

        await this.unsubscribeToMQTTAddresses();

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 2.",
        );
        for (const entry of Array.from(
          new Map(
            Object.entries(this._mqttServiceNative.observables),
          ).entries(),
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 2 Entry:  " + entry[0],
          );
        }
      }),
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#endregion

  //#region MQTT-address handling
  async subscribeMQTTAddresses(): Promise<void> {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    /**
     * GF - 2020-09-15: when user permissions change we clean the storage of the user and make the logout
     */

    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_USER_PERMISSION_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable.subscribe(
      async (message: IMqttMessage) => {
        if (
          this._username ===
          JSON.parse(message.payload.toString()).Message.Value
        ) {
          const resultAction: boolean = await this._alertService.presentAlertOk(
            this._lblChangedPermissionTitle,
            "",
            this._lblChangedPermissionText,
          );

          if (resultAction) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "subscribeMQTTAddresses()",
              "Changed permissions for user. Executing LOGOUT.",
            );
            await this._authService.logout();
          }
        }
      },
    );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription,
    );
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription,
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck,
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription,
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck,
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries(),
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  /**
   * Handle current user data and show aside menu and footer
   * based on current url.
   * It originally subscribe to userCredentials observable, handle current session data(via tap operator)
   * and finaly switch to router observable (via switchMap)
   */
  private handleUserRoutesAndData() {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser),
        tap((currentUser: CurrentSessionData) => {
          this._currentLoggedUser = currentUser;

          if (currentUser) {
            this._username = this._securetyDataProtectionService.decypt(
              currentUser.username,
              null,
            );
          }
        }),
        tap(() => this.setStatusBarAndToolbarColor()),
        switchMap(() => {
          return this._router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
          );
        }),
      )
      .subscribe((event: NavigationEnd) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_authService",
          "userCredentials - REACT TO navigation event",
          "We call handleFooter. NavigationStart: " + event.url,
        );
        this.handleFooterMenuOnReloadSystem(event.url);
      });
  }

  /**
   * Handle hide/show of menu and footer on entire reload system (like as pressing F5 button)
   */
  private handleFooterMenuOnReloadSystem(currentUrl: string) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleFooterMenuOnReloadSystem",
      "currentUrl: " + currentUrl,
    );
    if (
      currentUrl === "/" ||
      currentUrl === "/home" ||
      currentUrl.startsWith("/plant") ||
      (!this._currentLoggedUser && currentUrl === AppSettings.PATH_TO_SETTINGS)
    ) {
      this.changeShowMenuAside(false);
      this.changeShowStatusBar(false);
    } else {
      /*If the kioskMode has been enabled...  */
      if (this._currentClientSetting.kioskMode) {
        //If we are still in "options-settings", then we should still be able to log-out or go to "Supervisions" through the menu
        if (currentUrl === AppSettings.PATH_TO_SETTINGS) {
          this.changeShowMenuAside(true);

          //Otherwise, the lateral menu should be disabled.
        } else {
          this.changeShowMenuAside(false);
        }
      } else {
        this.changeShowMenuAside(true);
        this.changeShowStatusBar(true);
      }
    }
  }

  private subscribeNetworkServiceData(): void {
    this._networkService.currentConnectionType.subscribe(
      (currentConnectionType: ConnectionType) => {
        this._currentConnectionType = currentConnectionType;

        this.setStatusBarAndToolbarColor();
      },
    );
  }

  /**
   * Set the proximity sensor per Q5
   */
  private checkBrightnessWithProximitySensor() {
    if (this._bigService.isMyToDevice && this._bigService.isQ5Model) {
      if (
        this._currentClientSetting.enableBrightnessSettings &&
        this._currentClientSetting.typeBrightnessSettings ==
          TypeBrightness.PROXIMITY_SENSOR
      ) {
        this._proximitySensor = true;
        this._bigService.setProximitySensor(
          true,
          this._currentClientSetting.timeBrightness,
          this._currentClientSetting.percentageBrightness,
          this._currentClientSetting.defaultBrightness,
        );
      } else {
        if (this._proximitySensor) {
          this._bigService.setProximitySensor(
            false,
            this._currentClientSetting.timeBrightness,
            this._currentClientSetting.percentageBrightness,
            this._currentClientSetting.defaultBrightness,
          );
        }
        this._proximitySensor = false;
      }
    }
  }

  /**
   * Subsribe to public Observable "clientSettings" of "settings.service", which propagates the info about the general app
   * -related settings as defined for this device; it will also emit each time these settings are changed.
   */
  private subscribeSettingServiceData(): void {
    this._selectClientSettingsSub = this._store
      .select(clientSettings)
      .pipe(filter((clientSetting) => clientSetting !== null))
      .subscribe((clientSettings: ClientSettings) => {
        this._currentClientSetting = clientSettings;
        this.checkBrightnessWithProximitySensor();
        this.checkDefaultBrightness();
        /* If the app has just been launched, do these clientSettings-dependent actions */
        if (this._isInInitialisation) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_settingService",
            "clientSettings",
            "Client-settings returned: " + clientSettings,
          );
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_settingService",
            "clientSettings",
            "IS SIBO DEVICE?: " + this._bigService.isMyToDevice,
          );

          this._isInInitialisation = false;
        }

        this.checkKioskMode();
      });
  }

  private subscribeUserSettings(): void {
    this._selectServerSettingsSub = this._store
      .select(serverSettings)
      .subscribe((userSettings: Settings) => {
        this._currentUserSettings = userSettings;

        /* Peculiar actions based on changes to values of setting's peculiar properties */
        if (userSettings) {
          this.actOnShowStatusBar();
        }
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectCurrentUserSub.unsubscribe();
    this._selectClientSettingsSub.unsubscribe();
    this._selectServerSettingsSub.unsubscribe();
  }

  //#endregion

  /**
   * React to changes to settings' "ShowStatusBar" property
   */
  private actOnShowStatusBar(): void {
    const currentShowStatusBar: boolean = StaticClass.booleanize(
      this._currentUserSettings.ShowStatusBar,
    );

    if (currentShowStatusBar !== this._lastShowStatusBar) {
      this._lastShowStatusBar = currentShowStatusBar;
      this.changeShowStatusBar(this._lastShowStatusBar);
    }
  }

  /**
   * Handle the reaction to enablement/disablement of Kiosk Mode; if it is enable, the lateral menu shall be removed. Otherwise,
   * it shall be present.
   */
  private checkKioskMode(): void {
    //If we are still in "options", then we should still be able to log-out or go to "Supervisions" through the menu
    if (location.pathname === AppSettings.PATH_TO_SETTINGS) {
      this.changeShowMenuAside(true);

      //Otherwise, the lateral menu should be unabled/disabled according to toggle-button.
    } else {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkKioskMode",
        "NOT in Options",
      );
      this.changeShowMenuAside(!this._currentClientSetting.kioskMode);
    }
  }

  /**
   * Handle in peculiar manner backbutton on mobile devices.
   *
   * NOTE 1: This callBack to the press of backButton has the smallest priority possible (IE. 1); this means it
   * shall be invoked ONLY when no other callBack has been defined in the peculiar page/component/service.
   *
   * NOTE 2: We must call this method inside the callback of "this.platform.ready()", since only then it shall be possible to harness the native features.
   */
  private handleBackButton(): void {
    this._backButtonSubscription =
      this._platform.backButton.subscribeWithPriority(1, () => {
        const canGoBack: boolean = this.ionRouterOutlet.canGoBack();
        const isKioskModeActive =
          this._currentClientSetting?.kioskMode || false;

        if (this._currentLoggedUser) {
          if (canGoBack && !isKioskModeActive) {
            this._navController.pop();
          } else {
            //When the app is logged AND the kioskMode is active, then we want to remain on the current page (IE. the current Supervision)
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "handleBackButton",
              "BACKBUTTON DO NOTHING IF KIOSKMODE: " +
                isKioskModeActive +
                " OR canGoBack: " +
                canGoBack,
            );
          }
        } else {
          //Do nothing; the backButton is therefore functionally disabled when the app is NOT logged in to a plant.
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "handleBackButton",
            "BACKBUTTON DO NOTHING",
          );
        }
      });
  }

  /**
   * This method check if the NgRx store has already been initialised (IE. hydrated) from the local storage.
   * It does so observing the value of the "hydrated" feature; the method shall not teminate until this feature
   * change its value to TRUE.
   */
  private async checkIsStoreReady(): Promise<boolean> {
    return this._store
      .select(isHydrated)
      .pipe(
        filter((isHydrated: boolean) => isHydrated),
        take(1),
      )
      .toPromise();
  }

  /**
   * This method forces Angular to check if there has been a recent chnage in page-backing data.
   *
   * ATTENTION: This should be used ONLY when a immediate data-binding-reaction is needed (EX: when DOM should change just before the page is left.)
   */
  private refreshDOM(): void {
    this._dc.detectChanges();
  }

  /**
   * Add space when keyboard is opened on Android >= 15 in order to prevent hiding inputs using cordova ionic keyboard plugin
   */
  private addSpaceForVirtualKeyboard() {
    window.addEventListener("keyboardWillShow", (event: any) => {
      const ionContent = document.querySelector(
        "#main-content .ion-page:not(.ion-page-hidden) ion-content",
      ) as HTMLElement;
      ionContent.style.setProperty(
        "--keyboard-offset",
        `${event.keyboardHeight}px`,
      );
    });

    window.addEventListener("keyboardDidShow", () => {
      document.activeElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    window.addEventListener("keyboardWillHide", () => {
      const ionContent = document.querySelector(
        "#main-content .ion-page:not(.ion-page-hidden) ion-content",
      ) as HTMLElement;
      ionContent.style.setProperty("--keyboard-offset", `0px`);
    });
  }
}
